import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import MomentUtils from "@date-io/moment";
import moment, { Moment } from "moment";
import { toast } from "react-toastify";

import Grid from "@material-ui/core/Grid";
import Checkbox from "@material-ui/core/Checkbox";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import Collapse from "@material-ui/core/Collapse";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CircularProgress from "@material-ui/core/CircularProgress";
import Button from "@material-ui/core/Button";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import ToggleButton from "@material-ui/lab/ToggleButton";
import { Divider } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { MuiPickersUtilsProvider, DatePicker, TimePicker } from "@material-ui/pickers";
import { createStyles, makeStyles, Theme } from "@material-ui/core/styles";

import {
  borderColor,
  primaryColor,
  cardBackgroundColor,
  backgroundDefaultColor,
} from "../../../../assets/theme/palette";
import DateRangePicker from "./DateRangePicker";
import { getAllSites } from "../../../Utils/API/apiSettings";
import { getAllUsersWithRegisteredFace } from "../../../Utils/API/apiUsers";
import { UserFullNameModel } from "../../../Utils/Models/users";
import { SiteModel, ZoneModel } from "../../../Utils/Models/settings";
import { UserIdQueryType, CrowdEventLogFilter } from "../../../Utils/Models/api";
import { setCrowdFilter as setCrowdFilterAction } from "../../../../store/actions/people_filter_actions";
import { initialState } from "../../../../store/reducers/people_filter_reducer";
import { DeviceModel } from "../../../Utils/Models/devices";
import { TimezoneIdToOffset } from "../../../Utils/Constants/timezones";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      paddingLeft: "10px",
      paddingRight: "10px",
    },
    filterHeader: {
      display: "flex",
      justifyContent: "space-between",
      marginTop: "5px",
    },
    paddingRight: {
      paddingRight: "15px",
    },
    divider: {
      backgroundColor: borderColor,
      marginBottom: "20px",
      marginTop: "18px",
      width: "100%",
    },
    toggleButton: {
      backgroundColor: cardBackgroundColor + " !important",
      color: "white !important",
      height: "30px",
      marginBottom: "10px",
      width: "50px",

      "&.MuiToggleButton-root.Mui-selected": {
        backgroundColor: primaryColor + " !important",
        color: "white !important",
      },
    },
    inputContainer: {
      "& .MuiOutlinedInput-root": {
        alignItems: "start",
        display: "flex",
        flexDirection: "column",
        justifyContent: "start",
        backgroundColor: backgroundDefaultColor + " !important",
      },
    },
    textInput: {
      "& .MuiAutocomplete-input": {
        width: "auto",
      },
    },
    customRoot: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: backgroundDefaultColor + " !important",
      },
    },
    customPaper: {
      backgroundColor: cardBackgroundColor + " !important",
      margin: 0,
    },
    customPopper: {
      border: "1px solid white !important",
      borderRadius: "5px",
      marginTop: "5px",
      "& .MuiAutocomplete-option": {
        "&:hover": {
          backgroundColor: primaryColor + " !important",
        },
      },
    },
    customListbox: {
      padding: 0,
    },
    doneButton: {},
    dateLabelRoot: {
      marginLeft: 0,
      userSelect: "none",
    },
  })
);

const FilterActivity = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [loading, setLoading] = useState<boolean>(true);
  const [usersFullName, setUsersFullName] = useState<UserFullNameModel[]>([]);
  const [sites, setSites] = useState<SiteModel[]>([]);
  const [zones, setZones] = useState<ZoneModel[]>([]);
  const [devices, setDevices] = useState<DeviceModel[]>([]);
  const [crowdFilter, setCrowdFilter] = useState<CrowdEventLogFilter>(initialState);
  const [includeDate, setIncludeDate] = useState<boolean>(false);
  const [startDate, setStartDate] = useState<Moment>(moment().startOf("day"));
  const [endDate, setEndDate] = useState<Moment>(moment().endOf("day"));
  const [andOrValue, setAndOrValue] = useState<string>("and");
  const [siteValue, setSiteValue] = useState<SiteModel | null>(null);
  const [zoneValue, setZoneValue] = useState<ZoneModel | null>(null);
  const [deviceValue, setDeviceValue] = useState<DeviceModel | null>(null);

  useEffect(() => {
    const load = async () => {
      const allResponses = await Promise.all([getAllUsersWithRegisteredFace(), getAllSites(true)]);

      const usersResponse = allResponses[0];
      const sitesResponse = allResponses[1];

      if (!usersResponse.data.hasErrors) {
        setUsersFullName(usersResponse.data.payload);
      }

      if (!sitesResponse.data.hasErrors) {
        const sites = sitesResponse.data.payload;
        setSites(sites);

        const zones = sites.map((site: SiteModel) => [...site.zones]).flat();
        setZones(zones);

        const devices = zones
          .map((zone: ZoneModel) => [
            ...(zone.primaryDevices || []),
            ...(zone.secondaryDevices || []),
          ])
          .flat();
        setDevices(devices);
      }

      setLoading(false);
    };

    load();
  }, []);

  useEffect(() => {
    if (!includeDate) {
      setCrowdFilter((prev) => ({
        ...prev,
        fromDate: undefined,
        toDate: undefined,
      }));
    }
  }, [includeDate]);

  const dispatch = useDispatch();

  const createFilterQuery = () => {
    let dateAdjustedFilter = { ...crowdFilter };

    if (includeDate) {
      if (endDate < startDate) {
        toast.error(t("DateError"));
        return;
      }
      const sYear = startDate.year();
      const sMonth = String(startDate.month() + 1).padStart(2, "0");
      const sDate = String(startDate.date()).padStart(2, "0");
      const sHour = String(startDate.hour()).padStart(2, "0");
      const sMinute = String(startDate.minute()).padStart(2, "0");

      const eYear = endDate.year();
      const eMonth = String(endDate.month() + 1).padStart(2, "0");
      const eDate = String(endDate.date()).padStart(2, "0");
      const eHour = String(endDate.hour()).padStart(2, "0");
      const eMinute = String(endDate.minute()).padStart(2, "0");

      const startDateStr = new Date(
        `${sYear}-${sMonth}-${sDate}T${sHour}:${sMinute}:00${TimezoneIdToOffset(
          siteValue?.timezoneId
        )}`
      ).toISOString();

      const endDateStr = new Date(
        `${eYear}-${eMonth}-${eDate}T${eHour}:${eMinute}:00${TimezoneIdToOffset(
          siteValue?.timezoneId
        )}`
      ).toISOString();

      dateAdjustedFilter = {
        ...dateAdjustedFilter,
        fromDate: startDateStr,
        toDate: endDateStr,
      };
    }

    dispatch(setCrowdFilterAction(dateAdjustedFilter));
  };

  return (
    <Grid container className={classes.container}>
      <Grid container className={classes.filterHeader}>
        <Typography variant="h6">{t("FilterActivity")}</Typography>
        <Button
          color="primary"
          size="small"
          variant="contained"
          classes={{
            root: classes.doneButton,
          }}
          onClick={createFilterQuery}
        >
          {t("Apply")}
        </Button>
      </Grid>
      <Divider className={classes.divider} />
      <Grid className={classes.paddingRight}>
        <ToggleButtonGroup
          value={andOrValue}
          exclusive
          onChange={(e, value) => {
            if (value !== null) {
              setAndOrValue(value);
              setCrowdFilter((prev) => ({
                ...prev,
                userIdQueryType: value === "or" ? UserIdQueryType.Or : UserIdQueryType.And,
              }));
            }
          }}
        >
          <ToggleButton className={classes.toggleButton} value="and">
            <Typography variant="body2">AND</Typography>
          </ToggleButton>
          <ToggleButton className={classes.toggleButton} value="or">
            <Typography variant="body2">OR</Typography>
          </ToggleButton>
        </ToggleButtonGroup>
        <Autocomplete
          classes={{
            root: classes.inputContainer,
            paper: classes.customPaper,
            popper: classes.customPopper,
            listbox: classes.customListbox,
          }}
          multiple
          openOnFocus
          id="user-names"
          loading={loading}
          options={usersFullName}
          getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
          fullWidth
          onChange={(event: any, newValue: any) => {
            setCrowdFilter((prev) => ({
              ...prev,
              userIds: newValue
                .map(
                  (item: UserFullNameModel) =>
                    item.userId || item.guestUserId || item.personOfInterestUserId
                )
                .join(","),
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textInput}
              fullWidth
              variant="outlined"
              label={t("Name")}
              placeholder={t("Name")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        <Divider className={classes.divider} />
        <Autocomplete
          classes={{
            root: classes.customRoot,
            paper: classes.customPaper,
            popper: classes.customPopper,
            listbox: classes.customListbox,
          }}
          openOnFocus
          id="site"
          loading={loading}
          options={sites}
          getOptionLabel={(option) => `${option.name}`}
          fullWidth
          onChange={(event: any, newValue: SiteModel | null) => {
            let zones;

            setSiteValue(newValue); // todo: create endpoint to see data here

            if (newValue) {
              zones = newValue.zones;
            } else {
              setIncludeDate(false);
              zones = sites.map((site: SiteModel) => [...site.zones]).flat();
            }

            setZones(zones);

            const devices = zones
              .map((zone: ZoneModel) => [
                ...(zone.primaryDevices || []),
                ...(zone.secondaryDevices || []),
              ])
              .flat();

            setDevices(devices);
            setZoneValue(null);
            setDeviceValue(null);
            setCrowdFilter((prev) => ({
              ...prev,
              siteId: newValue?.id,
              zoneId: undefined,
              serialNumber: undefined,
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textInput}
              fullWidth
              variant="outlined"
              label={t("Site")}
              placeholder={t("Site")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        <Collapse in={siteValue != null}>
          <FormControlLabel
            classes={{
              root: classes.dateLabelRoot,
            }}
            label={t("FilterByDate")}
            control={
              <Checkbox
                checkedIcon={<CheckBoxIcon color="primary" fontSize="small" />}
                edge="start"
                checked={includeDate}
                disableRipple
                size={"small"}
                id="checkBox-date"
                onClick={() => setIncludeDate(!includeDate)}
              />
            }
          ></FormControlLabel>
          <Collapse in={includeDate}>
            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={(newValue) => setStartDate(newValue)}
              onEndDateChange={(newValue) => setEndDate(newValue)}
            />
          </Collapse>
        </Collapse>
        <Divider className={classes.divider} />
        <Autocomplete
          classes={{
            root: classes.customRoot,
            paper: classes.customPaper,
            popper: classes.customPopper,
            listbox: classes.customListbox,
          }}
          openOnFocus
          id="zone"
          loading={loading}
          options={zones}
          getOptionLabel={(option) => `${option.name}`}
          fullWidth
          value={zoneValue}
          onChange={(event: any, newValue: ZoneModel | null) => {
            let devices;

            setZoneValue(newValue);

            if (newValue) {
              devices = [
                ...(newValue?.primaryDevices || []),
                ...(newValue?.secondaryDevices || []),
              ];
            } else {
              if (siteValue) {
                devices = siteValue.zones
                  .map((zone) => [...(zone.primaryDevices || []), ...(zone.secondaryDevices || [])])
                  .flat();
              } else {
                devices = sites
                  .map((site) => site.zones)
                  .flat()
                  .map((zone) => [...(zone.primaryDevices || []), ...(zone.secondaryDevices || [])])
                  .flat();
              }
            }

            setDevices(devices);
            setDeviceValue(null);
            setCrowdFilter((prev) => ({
              ...prev,
              zoneId: newValue?.id,
              serialNumber: undefined,
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textInput}
              fullWidth
              variant="outlined"
              label={t("Zone")}
              placeholder={t("Zone")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
        <Divider className={classes.divider} />
        <Autocomplete
          classes={{
            root: classes.customRoot,
            paper: classes.customPaper,
            popper: classes.customPopper,
            listbox: classes.customListbox,
          }}
          openOnFocus
          id="device"
          loading={loading}
          options={devices}
          getOptionLabel={(option) => `${option.name}`}
          fullWidth
          value={deviceValue}
          onChange={(event: any, newValue: DeviceModel | null) => {
            setDeviceValue(newValue);
            setCrowdFilter((prev) => ({
              ...prev,
              serialNumber: newValue?.serialNumber,
            }));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              className={classes.textInput}
              fullWidth
              variant="outlined"
              label={t("Device")}
              placeholder={t("Device")}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              }}
            />
          )}
        />
      </Grid>
    </Grid>
  );
};

export default FilterActivity;
